import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { approvalFilter } from '@/recoil/atom';
import { PeriodStatus, StatusFilterName } from '@/types/api';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { getPeriodList } from '@/utils/date';
import { getFormattedToday } from '@/utils/format';
import DateRangePicker from '@/components/Approval/@components/DateRangePicker';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';

type PeriodFilterPanelProps = {
  pageArea: WorkPageAreaType;
  title: string;
  filterName: StatusFilterName;
};

const DIRECT_TYPE = 3;

/**
 * PeriodFilter 편집 본문 — 시안: 세로 라디오 리스트(단일 선택).
 * '직접 선택'을 고르면 하단에 시작~종료 날짜 입력이 나타난다.
 * 값은 전역 approvalFilter.endAuthoredAt(단일 요소 배열)을 직접 갱신.
 */
const PeriodFilterPanel = ({ pageArea, title }: PeriodFilterPanelProps) => {
  const { t } = useTranslation();
  const workEventTracker = useWorkEventTracker(pageArea);
  const [filters, setFilters] = useAtom(approvalFilter);

  const periodList = getPeriodList();
  const selected = filters.endAuthoredAt[0];
  // 날짜를 모두 지우면 필터에서는 빠지지만, 패널이 열려 있는 동안은 다시 입력할 수 있게 기억한다
  const [directMode, setDirectMode] = useState(
    selected?.type === DIRECT_TYPE,
  );

  // 라디오는 단일 선택이라 이미 선택된 항목을 다시 눌러도 해제하지 않는다
  // (직접 선택해 둔 날짜가 프리셋 기본값으로 되돌아가지 않도록 아무 동작도 하지 않는다.
  //  날짜를 모두 지워 filters 가 빈 상태여도 라디오는 체크돼 보이므로 directMode 도 함께 본다)
  const select = (res: PeriodStatus) => {
    const alreadyChecked = selected
      ? selected.type === res.type
      : directMode && res.type === DIRECT_TYPE;
    if (alreadyChecked) return;
    setDirectMode(res.type === DIRECT_TYPE);
    setFilters({
      ...filters,
      endAuthoredAt: [
        {
          name: res.name,
          type: res.type,
          value: res.value,
          value2: res.value2,
        },
      ],
    });
    // GA Work - Approval 12번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: title,
      contents: res.name,
      status: true,
      description: '품의 필터',
    });
  };

  const directPreset = periodList.find((res) => res.type === DIRECT_TYPE);

  const setDate = (which: 'start' | 'end', value: string) => {
    setFilters((prev) => {
      // 둘 다 지운 뒤 다시 입력하면 필터가 비어 있으므로 '직접 선택' 정보부터 채운다
      const base = prev.endAuthoredAt[0] ?? {
        name: directPreset?.name ?? '',
        type: DIRECT_TYPE,
        value: '',
        value2: '',
      };
      const next = {
        ...base,
        ...(which === 'start' ? { value } : { value2: value }),
      };
      // 시작·종료 둘 다 비면 조회 조건에서 빼되(칩/개수 오염 방지), 입력칸은 directMode 로 유지된다
      const bothEmpty = !next.value && !next.value2;
      return {
        ...prev,
        endAuthoredAt: bothEmpty ? [] : [next],
      };
    });
  };

  const isDirect = directMode || selected?.type === DIRECT_TYPE;
  // 달력도 키보드 입력과 같은 규칙(과거 제한 없음, 시작~종료 1년 이내)을 따르도록 min 은 두지 않는다
  const max = getFormattedToday();

  return (
    <div>
      <List>
        {periodList.map((res) => {
          // 날짜를 모두 지워 필터가 비어도 '직접 선택' 라디오는 선택된 채로 둔다
          const checked = selected
            ? selected.type === res.type
            : directMode && res.type === DIRECT_TYPE;
          return (
            <Row
              key={`periodList-${res.type}`}
              className={'v2rd-hover-target'}
              onClick={() => select(res)}
            >
              <RadioV2 checked={checked} sx={{ padding: 0 }} />
              <Label>{res.name}</Label>
            </Row>
          );
        })}
      </List>
      {isDirect && (
        <>
          <Line />
          <DateWrap>
            <DateRangePicker
              start={selected?.value}
              end={selected?.value2}
              max={max}
              onChange={setDate}
            />
          </DateWrap>
        </>
      )}
    </div>
  );
};

export default PeriodFilterPanel;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
`;

const Line = styled.div`
  height: 1px;
  background: var(--dk-bg-strong, #e7e9ec);
  margin: 8px 0;
`;

const DateWrap = styled.div`
  padding: 8px;
`;
